'use client';

import { Box, Popover as ChakraPopover, Field, PopoverAnchor, PopoverPositioner, Portal } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import FilterOptionsList from './components/FilterOptionsList';
import FilterSelectLabel from './components/FilterSelectLabel';
import FilterSelectTrigger from './components/FilterSelectTrigger';
import NoOptionsMessage from './components/NoOptionsMessage';
import SearchInput from './components/SearchInput';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterSelectProps {
  label: string;
  options: FilterOption[];
  selectedValues: FilterOption[];
  requiredMark?: boolean;
  onChange: (values: FilterOption[]) => void;
  placeholder?: string;
  isDisabled?: boolean;
  maxWidth?: string;
  width?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  debouncedSearch?: boolean;
}

const DEFAULT_PLACEHOLDER = 'Select options';
const DEFAULT_MAX_WIDTH = '300px';
const DEFAULT_WIDTH = '100%';

const useSearchState = (externalSearchValue?: string, onExternalSearchChange?: (value: string) => void) => {
  const [internalSearchTerm, setInternalSearchTerm] = useState('');

  const searchTerm = externalSearchValue !== undefined ? externalSearchValue : internalSearchTerm;

  const setSearchTerm = useCallback(
    (value: string) => {
      if (onExternalSearchChange) {
        onExternalSearchChange(value);
      } else {
        setInternalSearchTerm(value);
      }
    },
    [onExternalSearchChange]
  );

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, [setSearchTerm]);

  return { searchTerm, setSearchTerm, clearSearch };
};

const usePopoverState = (onSearchClear?: () => void) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => {
    setIsOpen(false);
    onSearchClear?.();
  }, [onSearchClear]);
  const toggle = useCallback(() => setIsOpen(!isOpen), [isOpen]);

  return { isOpen, open, close, toggle };
};

export const FilterSelect = ({
  label,
  options,
  selectedValues,
  requiredMark,
  onChange,
  placeholder = DEFAULT_PLACEHOLDER,
  isDisabled = false,
  maxWidth = DEFAULT_MAX_WIDTH,
  width = DEFAULT_WIDTH,
  showSearch = false,
  searchPlaceholder = 'Search options...',
  searchValue,
  onSearchChange,
  debouncedSearch = false,
}: FilterSelectProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const { searchTerm, setSearchTerm, clearSearch } = useSearchState(searchValue, onSearchChange);
  const { isOpen, open, close, toggle } = usePopoverState(clearSearch);

  const filteredOptions = useMemo(
    () => options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase())),
    [options, searchTerm]
  );

  const selectedCount = selectedValues.length;
  const displayText = placeholder;

  const handleToggleOption = useCallback(
    (value: string) => {
      const newValues = selectedValues.some((v) => v.value === value)
        ? selectedValues.filter((v) => v.value !== value)
        : [...selectedValues, { value, label: options.find((o) => o.value === value)?.label || value }];

      onChange(newValues);
      open();
    },
    [selectedValues, options, onChange, open]
  );

  const handleClearAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange([]);
      clearSearch();
    },
    [onChange, clearSearch]
  );

  const handleOpenChange = useCallback(
    (details: { open: boolean }) => {
      if (details.open) {
        open();
      } else {
        close();
      }
    },
    [open, close]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideTrigger = triggerRef.current && !triggerRef.current.contains(target);
      const isOutsidePopover = popoverRef.current && !popoverRef.current.contains(target);

      if (isOutsideTrigger && isOutsidePopover) {
        close();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [close]);

  return (
    <Box maxWidth={maxWidth} width={width}>
      <Field.Root>
        <FilterSelectLabel label={label} requiredMark={requiredMark} />

        <ChakraPopover.Root open={isOpen} onOpenChange={handleOpenChange} modal={false}>
          <PopoverAnchor width={width}>
            <ChakraPopover.Trigger asChild>
              <FilterSelectTrigger
                isOpen={isOpen}
                displayText={displayText}
                selectedCount={selectedCount}
                onToggle={toggle}
                onClearAll={handleClearAll}
                isDisabled={isDisabled}
                triggerRef={triggerRef}
              />
            </ChakraPopover.Trigger>
          </PopoverAnchor>

          <Portal>
            <PopoverPositioner>
              <ChakraPopover.Content
                ref={popoverRef}
                width={width}
                minWidth={width}
                maxWidth={maxWidth}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="8px"
                boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1)"
                backgroundColor="white"
              >
                <Box p={3}>
                  {showSearch && (
                    <SearchInput
                      placeholder={searchPlaceholder}
                      value={searchTerm}
                      onChange={setSearchTerm}
                      debounced={debouncedSearch}
                      onDebouncedChange={onSearchChange}
                    />
                  )}
                  {filteredOptions.length > 0 ? (
                    <FilterOptionsList options={filteredOptions} selectedValues={selectedValues} onToggle={handleToggleOption} />
                  ) : (
                    <NoOptionsMessage />
                  )}
                </Box>
              </ChakraPopover.Content>
            </PopoverPositioner>
          </Portal>
        </ChakraPopover.Root>
      </Field.Root>
    </Box>
  );
};

export default FilterSelect;
